import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { RefreshCw, CheckCircle2, Clock, FileDown, Landmark, Calendar } from "lucide-react";

const fmtEur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString("it-IT") : "—";

const STATO = {
  in_attesa_pagamento: { l: "In attesa pagamento", c: "bg-blue-100 text-blue-800", icon: Clock },
  pagato: { l: "Pagato", c: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
};

/** Iter122bi · SuperAdmin · Tab "Rinnovi annuali" (piano per_condominio)
 * Iter122bj · embeddable dentro tenant card con prop `tenantId` (nasconde header, no genera).
 */
export default function SuperAdminRinnoviAnnuali({ tenantId = null, compact = false }) {
  const [items, setItems] = useState([]);
  const [anno, setAnno] = useState(new Date().getFullYear());
  const [busy, setBusy] = useState(false);
  const [refCRO, setRefCRO] = useState({});

  const load = () => api.get(`/superadmin/rinnovi-annuali`, {
    params: { ...(anno ? { anno } : {}), ...(tenantId ? { tenant_id: tenantId } : {}) },
  }).then((r) => setItems(r.data || [])).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anno, tenantId]);

  const generaOra = async () => {
    if (!window.confirm(`Genera note di rinnovo per l'anno ${anno}? (una nota per ogni studio con piano per_condominio)`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/rinnovi-annuali/genera?anno=${anno}`, {});
      toast.success(`Generate ${data.generati} note di rinnovo per il ${data.anno}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };

  const marcaPagato = async (n) => {
    const ref = refCRO[n.id] || window.prompt("CRO bonifico / riferimento pagamento:");
    if (!ref) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/rinnovi-annuali/${n.id}/marca-pagato`, { metodo: "bonifico", riferimento: ref });
      toast.success("Rinnovo registrato come pagato");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    setBusy(false);
  };

  const scaricaPdf = async (n) => {
    try {
      const res = await api.get(`/superadmin/rinnovi-annuali/${n.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rinnovo-${n.anno}-${n.id.slice(0,8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore scaricamento PDF"); }
  };

  const totalePagato = items.filter(i => i.stato === "pagato").reduce((s, i) => s + Number(i.totale || 0), 0);
  const totaleAttesa = items.filter(i => i.stato === "in_attesa_pagamento").reduce((s, i) => s + Number(i.totale || 0), 0);

  return (
    <div className="space-y-4" data-testid="superadmin-rinnovi-annuali">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        {!compact && (
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-brand" />
            <h3 className="font-display text-lg font-bold text-slate-800">Rinnovi annuali · piano per_condominio</h3>
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-slate-500">Anno:</label>
          <Input type="number" value={anno} onChange={(e) => setAnno(parseInt(e.target.value) || new Date().getFullYear())}
                 className="h-9 w-24" data-testid="rinnovi-anno" />
          {!compact && (
            <Button size="sm" disabled={busy} onClick={generaOra} data-testid="rinnovi-genera" className="h-9 bg-brand hover:bg-brand-dark">
              <RefreshCw className="w-4 h-4 mr-1" /> Genera ora
            </Button>
          )}
        </div>
      </div>

      {!compact && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-3 bg-slate-50 border-2"><div className="text-xs text-slate-500 font-semibold uppercase">Note totali</div><div className="text-2xl font-bold text-slate-800">{items.length}</div></Card>
          <Card className="p-3 bg-emerald-50 border-2 border-emerald-200"><div className="text-xs text-emerald-600 font-semibold uppercase">Pagate</div><div className="text-2xl font-bold text-emerald-800">{fmtEur(totalePagato)}</div></Card>
          <Card className="p-3 bg-blue-50 border-2 border-blue-200"><div className="text-xs text-blue-600 font-semibold uppercase">In attesa</div><div className="text-2xl font-bold text-blue-800">{fmtEur(totaleAttesa)}</div></Card>
        </div>
      )}

      {items.length === 0 && <Card className="p-3 text-center text-slate-400 text-sm">Nessuna nota per l&apos;anno {anno}.</Card>}
      {items.map((n) => {
        const S = STATO[n.stato] || { l: n.stato, c: "bg-slate-100", icon: Clock };
        const Ic = S.icon;
        return (
          <Card key={n.id} className="p-4 border-2" data-testid={`rinnovo-${n.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-bold flex flex-wrap items-center gap-2 text-slate-800">
                  <span>Rinnovo {n.anno}{!compact && ` · ${n.tenant_nome || n.tenant_id}`}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${S.c}`}>
                    <Ic className="w-3 h-3" /> {S.l}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {n.num_condomini} condomìni × {fmtEur(n.prezzo_unit_annuo)}/anno · Imponibile {fmtEur(n.imponibile)} + IVA {n.iva_pct}% ({fmtEur(n.iva)}) = <b>{fmtEur(n.totale)}</b>
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  Periodo: {n.periodo_da} → {n.periodo_a} · Emessa il {fmtDate(n.created_at)}
                  {n.data_pagamento && <span className="text-emerald-700 ml-2">Pagato il {fmtDate(n.data_pagamento)} · {n.metodo_pagamento} {n.riferimento_pagamento ? `(${n.riferimento_pagamento})` : ""}</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                {n.stato === "in_attesa_pagamento" && (
                  <>
                    <Input placeholder="CRO bonifico" data-testid={`rinnovo-ref-${n.id}`}
                           value={refCRO[n.id] || ""}
                           onChange={(e) => setRefCRO((s) => ({ ...s, [n.id]: e.target.value }))}
                           className="h-9 w-40" />
                    <Button size="sm" disabled={busy} onClick={() => marcaPagato(n)} data-testid={`rinnovo-marca-pagato-${n.id}`} className="bg-brand hover:bg-brand-dark h-9">
                      <Landmark className="w-4 h-4 mr-1" /> Marca pagato
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => scaricaPdf(n)} data-testid={`rinnovo-pdf-${n.id}`} className="h-9">
                  <FileDown className="w-4 h-4 mr-1" /> PDF
                </Button>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
